'use client';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { apiClient } from '@/lib/api/client';
import type { BusinessType } from '@/types/business';

const businessTypes: { value: BusinessType; label: string; description: string }[] = [
  { value: 'retail',        label: 'Retail',          description: 'Shops, groceries, boutiques' },
  { value: 'food_beverage', label: 'Food & Beverage', description: 'Restaurants, cafes, bakeries' },
  { value: 'service',       label: 'Service',         description: 'Salons, repair, appointments' },
  { value: 'online',        label: 'Online',          description: 'Digital and ecommerce stores' },
  { value: 'other',         label: 'Other',           description: 'Custom business operations' },
];

interface BusinessCategoryOption {
  id: number;
  name: string;
  image_url: string | null;
}

const schema = z.object({
  business_name:        z.string().min(2, 'Business name is required'),
  business_type:        z.enum(['retail', 'food_beverage', 'service', 'online', 'other']),
  business_category_id: z.number().min(1, 'Business category is required'),
  business_phone:       z.string().optional(),
  business_email:       z.string().email('Enter a valid email').optional().or(z.literal('')),
});

export type BusinessInfoData = z.infer<typeof schema>;

interface StepBusinessInfoProps {
  defaultValues?: Partial<BusinessInfoData>;
  onNext: (data: BusinessInfoData) => void;
  onBack: () => void;
  isLoading?: boolean;
}

export function StepBusinessInfo({ defaultValues, onNext, onBack, isLoading }: StepBusinessInfoProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BusinessInfoData>({
    resolver: zodResolver(schema),
    defaultValues: {
      business_name:        '',
      business_type:        'retail',
      business_category_id: 0,
      business_phone:       '',
      business_email:       '',
      ...defaultValues,
    },
  });

  const selectedType     = watch('business_type') ?? 'retail';
  const selectedTypeMeta = businessTypes.find((t) => t.value === selectedType);
  const selectedCategoryId = watch('business_category_id');

  const [categories, setCategories] = useState<BusinessCategoryOption[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get('/business-categories-public/list')
      .then((res) => {
        setCategories(res.data?.data?.business_categories ?? []);
      })
      .catch(() => setCategories([]))
      .finally(() => setCategoriesLoading(false));
  }, []);

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);
  const categoryReady = !categoriesLoading && categories.length > 0;

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="business_name" className="text-sm font-medium text-slate-700">
          Business Name
        </Label>
        <Input
          id="business_name"
          placeholder="My Awesome Shop"
          className="h-11 rounded-xl border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-950 placeholder:text-slate-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-violet-500/40"
          {...register('business_name')}
        />
        {errors.business_name && (
          <p className="text-xs text-red-600">{errors.business_name.message}</p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Business Type</Label>
          <Select
            value={selectedType}
            onValueChange={(v) => setValue('business_type', v as BusinessType, { shouldValidate: true })}
          >
            <SelectTrigger className="h-11 w-full rounded-xl border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-950 focus:ring-2 focus:ring-violet-500/40">
              <SelectValue placeholder="Select a type" />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-slate-200 bg-white text-slate-950 shadow-lg">
              {businessTypes.map((type) => (
                <SelectItem key={type.value} value={type.value} label={type.label} className="text-sm">
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedTypeMeta && (
            <p className="text-xs text-slate-400">{selectedTypeMeta.description}</p>
          )}
          {errors.business_type && (
            <p className="text-xs text-red-600">{errors.business_type.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">
            Business Category <span className="text-red-500">*</span>
          </Label>
          <div className="flex items-center gap-3">
            {categoryReady ? (
              <Select
                value={selectedCategoryId?.toString() ?? ''}
                onValueChange={(v) => setValue('business_category_id', Number(v), { shouldValidate: true })}
                items={categories.map((c) => ({ value: c.id.toString(), label: c.name }))}
              >
                <SelectTrigger className="h-11 w-full flex-1 rounded-xl border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-950 focus:ring-2 focus:ring-violet-500/40">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-slate-200 bg-white text-slate-950 shadow-lg">
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()} label={cat.name} className="text-sm">
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="h-11 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-400 flex items-center">
                {categoriesLoading ? 'Loading categories\u2026' : 'Select a category'}
              </div>
            )}
            {selectedCategory?.image_url && (
              <img
                src={selectedCategory.image_url}
                alt={selectedCategory.name}
                className="h-10 w-10 shrink-0 rounded-lg object-cover border border-slate-200"
              />
            )}
          </div>
          {errors.business_category_id && (
            <p className="text-xs text-red-600">{errors.business_category_id.message}</p>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="business_phone" className="text-sm font-medium text-slate-700">
            Business Phone{' '}
            <span className="font-normal text-slate-400">(optional)</span>
          </Label>
          <Input
            id="business_phone"
            placeholder="+94 11 234 5678"
            className="h-11 rounded-xl border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-950 placeholder:text-slate-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-violet-500/40"
            {...register('business_phone')}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="business_email" className="text-sm font-medium text-slate-700">
            Business Email{' '}
            <span className="font-normal text-slate-400">(optional)</span>
          </Label>
          <Input
            id="business_email"
            type="email"
            placeholder="hello@myshop.lk"
            className="h-11 rounded-xl border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-950 placeholder:text-slate-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-violet-500/40"
            {...register('business_email')}
          />
          {errors.business_email && (
            <p className="text-xs text-red-600">{errors.business_email.message}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col-reverse gap-3 pt-1 sm:flex-row">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          className="h-11 flex-1 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Button
          type="submit"
          disabled={isLoading}
          className="h-11 flex-1 rounded-xl bg-violet-600 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 focus-visible:ring-2 focus-visible:ring-violet-500/50 disabled:opacity-60"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating workspace…
            </>
          ) : (
            'Create Account'
          )}
        </Button>
      </div>
    </form>
  );
}
